/**
 * The primitives, in every state.
 *
 * The design guide names this as the biggest gap the marketing site leaves: the
 * mockups are static and decorative, so hover, active, focus-visible, disabled,
 * loading, error and empty are all undefined. They are defined here, once, so no
 * screen invents them.
 *
 * Two Ledger laws are enforced structurally rather than by discipline:
 *
 *  - 3px radius on every surface, button and field (`rounded-card`). `rounded-pill`
 *    appears only on status chips, count badges, avatars and segmented controls.
 *  - Separation is a hairline, never a shadow. `--shadow-float` belongs to the
 *    marketing hero and has no place in the app.
 *
 * Skeletons are paper-toned hairline placeholders. Grey blocks would sit outside the
 * palette entirely, which is exactly how an app stops looking like the site.
 */
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { useId } from "react";
import type { IconName } from "@onecpa/tokens/icons";
import { Icon } from "./Icon.js";

// ------------------------------------------------------------------- button

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    "bg-ledger text-on-primary hover:bg-primary-strong active:bg-primary-strong disabled:bg-ledger/40",
  secondary:
    "border border-rule-strong bg-surface text-ink hover:border-ledger hover:text-ledger active:bg-wash disabled:border-rule disabled:text-ink-faint",
  ghost: "text-ink-muted hover:bg-wash hover:text-ink active:bg-wash disabled:text-ink-faint",
  // Reserved for destructive confirmation. Red is loss, never emphasis.
  danger:
    "border border-red-ink/30 bg-danger-bg text-danger-ink hover:border-red-ink active:bg-danger-bg disabled:opacity-50",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: "sm" | "md";
  loading?: boolean;
  icon?: IconName;
}

export function Button({
  variant = "secondary",
  size = "md",
  loading = false,
  icon,
  children,
  className = "",
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      // aria-busy rather than swapping the label: a screen reader user should hear
      // that the same action is in flight, not that the button became "טוען".
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      className={[
        // A control, so the 6px field radius rather than the 8px surface one.
        // The 1px press is the whole motion budget for a button: enough to feel
        // like it depressed, small enough that a dense toolbar does not wobble.
        "inline-flex items-center justify-center gap-1.5 rounded-field font-semibold",
        "transition-[color,background-color,border-color,translate] duration-100 ease-ledger",
        "active:translate-y-px disabled:active:translate-y-0",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
        "disabled:cursor-not-allowed",
        size === "sm" ? "px-2.5 py-1 text-xs" : "px-3.5 py-1.5 text-sm",
        BUTTON_VARIANTS[variant],
        className,
      ].join(" ")}
      {...rest}
    >
      {loading ? (
        <Spinner className="size-3.5" />
      ) : icon ? (
        <Icon name={icon} className="size-4" />
      ) : null}
      {children}
    </button>
  );
}

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: IconName;
  /** Required: an icon-only control with no accessible name is unusable. */
  label: string;
}

export function IconButton({ icon, label, className = "", ...rest }: IconButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={[
        "grid place-items-center rounded-field p-1.5 text-ink-muted transition-colors",
        "hover:bg-wash hover:text-ink",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
        "disabled:cursor-not-allowed disabled:opacity-40",
        className,
      ].join(" ")}
      {...rest}
    >
      <Icon name={icon} className="size-4" />
    </button>
  );
}

// -------------------------------------------------------------------- field

export interface FieldProps {
  label: string;
  /** Rendered under the control, and referenced by aria-describedby. */
  help?: string;
  error?: string;
  required?: boolean;
  children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
  className?: string;
}

/**
 * Label, help text, required marker and inline error — the full field anatomy.
 *
 * The ids are handed to the caller rather than cloned onto a child, so a field can
 * wrap any control (including a native select or a third-party editor) without this
 * component knowing what it is.
 */
export function Field({ label, help, error, required, children, className = "" }: FieldProps) {
  const id = useId();
  const helpId = `${id}-help`;
  const errorId = `${id}-error`;
  const describedBy =
    [help ? helpId : null, error ? errorId : null].filter(Boolean).join(" ") || undefined;

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <label htmlFor={id} className="text-xs font-semibold text-ink-muted">
        {label}
        {required && (
          <span className="ms-1 text-red-ink" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {children({ id, describedBy, invalid: Boolean(error) })}
      {help && !error && (
        <p id={helpId} className="text-2xs text-ink-faint">
          {help}
        </p>
      )}
      {error && (
        // The live region matters: a validation message that appears silently is
        // invisible to anyone not looking at that field.
        <p id={errorId} role="alert" className="text-2xs font-semibold text-red-ink">
          {error}
        </p>
      )}
    </div>
  );
}

const CONTROL_BASE =
  "w-full rounded-field border bg-surface px-2.5 py-1.5 text-sm text-ink transition-colors placeholder:text-ink-faint focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent disabled:cursor-not-allowed disabled:bg-wash disabled:text-ink-faint";

function controlClasses(invalid: boolean | undefined, className: string): string {
  return [
    CONTROL_BASE,
    invalid ? "border-red-ink/60" : "border-rule-strong hover:border-ink-faint",
    className,
  ].join(" ");
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export function Input({ invalid, className = "", ...rest }: InputProps) {
  return (
    <input
      aria-invalid={invalid || undefined}
      className={controlClasses(invalid, className)}
      {...rest}
    />
  );
}

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export function Textarea({ invalid, className = "", ...rest }: TextareaProps) {
  return (
    <textarea
      aria-invalid={invalid || undefined}
      className={controlClasses(invalid, `min-h-20 resize-y ${className}`)}
      {...rest}
    />
  );
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}

export function Select({ invalid, className = "", children, ...rest }: SelectProps) {
  return (
    <select
      aria-invalid={invalid || undefined}
      className={controlClasses(invalid, className)}
      {...rest}
    >
      {children}
    </select>
  );
}

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  label: string;
}

export function Checkbox({ label, className = "", ...rest }: CheckboxProps) {
  return (
    <label
      className={`inline-flex cursor-pointer items-center gap-2 text-sm text-ink ${className}`}
    >
      <input
        type="checkbox"
        className="size-3.5 rounded-[2px] border-rule-strong text-ledger accent-ledger focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ledger"
        {...rest}
      />
      {label}
    </label>
  );
}

/** A segmented control — the one place besides chips where the pill radius applies. */
export interface SegmentedProps<T extends string> {
  options: { value: T; label: string; count?: number }[];
  value: T;
  onChange(value: T): void;
  className?: string;
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  className = "",
}: SegmentedProps<T>) {
  return (
    <div className={`inline-flex rounded-pill bg-wash p-0.5 ${className}`} role="tablist">
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(option.value)}
            className={[
              "rounded-pill px-2.5 py-1 text-xs transition-colors",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ledger",
              active
                ? "bg-surface font-semibold text-ink shadow-card"
                : "text-ink-faint hover:text-ink-muted",
            ].join(" ")}
          >
            {option.label}
            {option.count !== undefined && (
              <span className="tnum ms-1 leading-none opacity-70">{option.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ------------------------------------------------------------------ states

export function Spinner({ className = "size-4" }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth={2.5} opacity={0.25} />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Paper-toned, hairline-bounded. Never a grey block. */
export function Skeleton({ className = "h-4 w-full" }: { className?: string }) {
  return (
    <span
      className={`block animate-pulse rounded-field border border-line-soft bg-wash/60 ${className}`}
      aria-hidden="true"
    />
  );
}

export function TableSkeleton({ rows = 6, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="ruled overflow-hidden" aria-busy="true" aria-label="טוען">
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div
          key={rowIndex}
          className="flex items-center gap-4 border-b border-line-soft px-4 py-2.5 last:border-0"
        >
          {Array.from({ length: columns }, (_, columnIndex) => (
            <Skeleton
              key={columnIndex}
              className={columnIndex === 0 ? "h-3.5 w-40" : "h-3.5 flex-1"}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export interface EmptyStateProps {
  title: string;
  /** What to do about it. An empty state with no next step is a dead end. */
  description?: string;
  icon?: IconName;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({
  title,
  description,
  icon = "search",
  action,
  className = "",
}: EmptyStateProps) {
  return (
    <div className={`grid place-items-center px-6 py-14 text-center ${className}`}>
      <Icon name={icon} className="size-6 text-ink-faint" />
      <p className="mt-3 text-base font-semibold text-ink">{title}</p>
      {description && <p className="mt-1 max-w-sm text-xs text-ink-muted">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export interface ErrorStateProps {
  title?: string;
  message: string;
  /** Shown in small print: it is what support will ask for. */
  requestId?: string;
  onRetry?: () => void;
  className?: string;
}

export function ErrorState({
  title = "לא ניתן לטעון את המידע",
  message,
  requestId,
  onRetry,
  className = "",
}: ErrorStateProps) {
  return (
    <div className={`grid place-items-center px-6 py-14 text-center ${className}`} role="alert">
      <p className="text-base font-semibold text-ink">{title}</p>
      <p className="mt-1 max-w-md text-xs text-ink-muted">{message}</p>
      {requestId && (
        <p className="mt-2 text-2xs text-ink-faint">
          מזהה בקשה: <bdi className="tnum">{requestId}</bdi>
        </p>
      )}
      {onRetry && (
        <Button variant="secondary" onClick={onRetry} className="mt-4">
          נסה שוב
        </Button>
      )}
    </div>
  );
}
